import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }

interface MoveEntry {
  name: string;
  type?: string;
  move_type?: string;  // "fast" | "charge"
  is_elite?: boolean;
  is_legacy?: boolean;
}

function MovePill({ move }: { move: MoveEntry }) {
  const badgeClass = move.type
    ? (TYPE_BADGE_COLORS[move.type.toLowerCase()] ?? "bg-white/10 text-white/60")
    : "bg-white/10 text-white/60";
  const category = move.move_type === "fast" ? "Fast" : move.move_type === "charge" ? "Charged" : null;

  return (
    <div className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-white/[0.04]">
      <span className="flex-1 text-sm text-white/80">{move.name}</span>
      {move.type && (
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badgeClass}`}>
          {move.type}
        </span>
      )}
      {category && (
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/40">
          {category}
        </span>
      )}
    </div>
  );
}

export default function EliteTMCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "elite_tm");
  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40 py-4 text-center">No Elite TM data available.</p>
      </CardShell>
    );
  }

  const eliteMoves: MoveEntry[] = Array.isArray(sec.elite_moves) ? (sec.elite_moves as MoveEntry[]) : [];
  const legacyMoves: MoveEntry[] = Array.isArray(sec.legacy_moves) ? (sec.legacy_moves as MoveEntry[]) : [];
  const hasAny = eliteMoves.length > 0 || legacyMoves.length > 0;

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {!hasAny ? (
        <p className="text-sm text-white/40 py-4 text-center">
          No Elite TM or legacy moves found.
        </p>
      ) : (
        <div className="space-y-5">
          {eliteMoves.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-wide text-white/30">Elite TM moves</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 font-medium">
                  Requires Elite TM
                </span>
              </div>
              <ul className="space-y-1">
                {eliteMoves.map((m) => <li key={m.name}><MovePill move={m} /></li>)}
              </ul>
            </div>
          )}

          {legacyMoves.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-wide text-white/30">Legacy moves</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 font-medium">
                  No longer obtainable
                </span>
              </div>
              <ul className="space-y-1">
                {legacyMoves.map((m) => <li key={m.name}><MovePill move={m} /></li>)}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-white/20 leading-relaxed pt-1">
            Elite TM moves require an Elite Fast TM or Elite Charged TM to teach.
            Legacy moves are no longer available through normal gameplay.
          </p>
        </div>
      )}
    </CardShell>
  );
}
